import React, { useEffect, useRef, useState } from 'react';
import Loading from '../../components/Loading';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { validateEmail } from '../../utils';
import axios from 'axios';
import TurnstileWidget from './TurnstileWidget';

const FORGOT_PASSWORD_COOLDOWN_SECONDS = 45;

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const turnstileRef = useRef();

  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const timer = setTimeout(() => {
      setCooldown(prev => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSend = async () => {
    if (loading) {
      return;
    }

    if (cooldown > 0) {
      toast.error(`Please wait ${cooldown}s before requesting again.`);
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    if (!validateEmail(normalizedEmail)) {
      toast.error('Please enter a valid email.');
      return;
    }

    if (!token) {
      toast.error('Please complete the CAPTCHA.');
      return;
    }

    try {
      setLoading(true);
      await axios.post(process.env.REACT_APP_BACKEND_URL + '/forgot-password', {
        email: normalizedEmail,
        verifyToken: token
      });

      toast.success('If this email is registered, a reset link was sent.');
      setEmail('');
      setToken('');
      setCooldown(FORGOT_PASSWORD_COOLDOWN_SECONDS);
      onClose();
    } catch (_err) {
      toast.error('Request could not be completed. Please try again.');
    } finally {
      setLoading(false);
      setToken('');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reset Password">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Please enter your email address. We’ll send you a reset link.
        </p>

        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          maxLength={254}
          className="w-full px-4 py-2 border rounded focus:outline-none focus:ring"
        />

        <TurnstileWidget onSuccess={setToken} ref={turnstileRef} />

        <button
          onClick={handleSend}
          disabled={loading || cooldown > 0}
          className="w-full bg-primary text-white py-2 rounded hover:bg-opacity-90 disabled:opacity-60"
        >
          {loading ? (
            <Loading type="inline" />
          ) : cooldown > 0 ? (
            `Try again in ${cooldown}s`
          ) : (
            'Send Reset Link'
          )}
        </button>
      </div>
    </Modal>
  );
};

ForgotPasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ForgotPasswordModal;
